import datapoints from '../datapoints';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { getApolloClient } from 'owa-apollo';
import * as Schema from 'owa-graph-schema';
import deleteConversationStoreUpdate from 'owa-mail-actions/lib/triage/deleteConversationStoreUpdate';
import { getRowIdsFromRowKeys } from 'owa-mail-list-store';
import TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import * as undoActions from 'owa-mail-undo';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import {
    SetInferenceClassificationOnConversationDocument,
    SetInferenceClassificationOnConversationMutation,
} from '../../graphql/__generated__/setInferenceClassificationOnConversationMutation.interface';

type SetInferenceClassificationOnConversationResult = SetInferenceClassificationOnConversationMutation['setInferenceClassificationOnConversation'];

/**
 * move to focused/other action
 * @param conversationRowKeys the specified rowKeys
 * @param tableViewId the table view id
 * @param actionSource the source that initiated this action
 * @return the action response
 */
export default createLegacyActionWithDatapoint(
    'moveConversationToFocusedOrOtherAction',
    datapoints.moveConversationToFocusedOrOther
)(async function moveConversationToFocusedOrOther(
    conversationRowKeys: string[],
    tableViewId: string,
    toViewFilter: FocusedViewFilter,
    actionSource: ActionSource
): Promise<SetInferenceClassificationOnConversationResult> {
    const tableView: TableView = listViewStore.tableViews.get(tableViewId);
    const isFocusedView: boolean = toViewFilter == FocusedViewFilter.Focused;
    const conversationIdStrings = getRowIdsFromRowKeys(conversationRowKeys, tableViewId);
    const datapoint = returnTopExecutingActionDatapoint();

    await undoActions.clearLastUndoableAction();

    const setInferenceClassificationPromise = invokeSetInferenceClassificationOnConversationMutation(
        conversationIdStrings,
        tableView.tableQuery.folderId,
        isFocusedView ? 'Focused' : 'Other'
    );

    datapoint.markUserPerceivedTime();

    // remove conversation from list view and reading pane store
    // VSO 20053:Tombstone not working when moving conversation from focused to other
    deleteConversationStoreUpdate(conversationIdStrings, tableView, 'Move');

    return setInferenceClassificationPromise;
});

/**
 * Invoke the SetInferenceClassificationOnConversation graphql mutation
 * @param conversationIds conversation Ids
 * @param contextFolderId the contextFolderId
 * @param inferenceClassification the inference classification to set
 * @return the SetInferenceClassificationOnConversationResult result
 */
async function invokeSetInferenceClassificationOnConversationMutation(
    conversationIds: string[],
    contextFolderId: string,
    inferenceClassification: Schema.InferenceClassification
): Promise<SetInferenceClassificationOnConversationResult> {
    const conversationSnapShots = conversationIds.map(conversationId => {
        return {
            id: conversationId,
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: contextFolderId,
            inferenceClassification: inferenceClassification,
        },
        mutation: SetInferenceClassificationOnConversationDocument,
    });
    return result?.data?.setInferenceClassificationOnConversation;
}
